import React from "react";
import { Box, Checkbox, CircularProgress, Sheet, Table } from "@mui/joy";
import CommonModal from "components/common/CommonModal";
import IconButtonCTA from "components/common/IconButtonCTA";
import DarkTypography from "components/common/DarkTypography";
import PurchaseProductModal from "components/shared/customerProducts/PurchaseProductModal";
import ExcursionCounterClientName, { purchaseProductAriaLabel } from "./ExcursionCounterClientName";
import { useAuth } from "contexts/api";
import { useBilling } from "contexts/api/billing";
import { useCustomer } from "contexts/api/customers";
import { useTraining } from "contexts/api/training";
import { useAlert } from "contexts/app/notificationToast";
import { useConfirmationModal } from "contexts/app/confirmation";
import { Permission } from "api/zekay/permissions";
import { usePermissions } from "contexts/app/permissions";
import useAppSettings from "contexts/app/appSettings";
import { userDisplayName } from "utils/userHelper";
import TrainingStatusActionSelect from "components/trainings/common/TrainingStatusActionSelect";
import { pendingStatusRowEmoji } from "components/trainings/common/trainingStatusActionTokens";
import type {
  PendingStatusAction,
  TrainingStatusAction,
} from "components/trainings/common/trainingStatusActionTokens";
import { trainingStatusMenuActions } from "components/trainings/common/trainingStatusMenuActions";
import { colorTokens, hrTableSx, textTokens } from "styles/globalStyles";
import { SCreate } from "styles/StyledMUI";
import type { CustomerProduct } from "api/billing/Billing.typed";
import type { Training } from "api/training/Training.typed";
import type {
  ExcursionCounterCustomerForBilling,
  ExcursionCounterValidationGroupSlice,
} from "../../../excursions/shared/utils/excursionUtils";

export type ExcursionCounterCustomerRow = ExcursionCounterCustomerForBilling;

type ProductColumn = { productId: number; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  excursionId?: number;
  excursionName: string;
  excursionDate: string;
  startTime?: string;
  endTime?: string;
  customerGroups: ExcursionCounterValidationGroupSlice[];
  onSuccess: () => void;
  /** When set, parent renders the profile action before each client name (opens CustomerOverviewModal). */
  onOpenCustomerOverview?: (customerId: number) => void;
};

function formatTimeForDisplay(t: string | undefined): string {
  const raw = (t || "").trim();
  if (!raw) return "";
  if (/^\d{1,2}:\d{2}/.test(raw)) {
    return raw.length >= 5 ? raw.slice(0, 5) : raw;
  }
  if (raw.includes("T")) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
  }
  return raw;
}

function buildExcursionContextSubtitle(
  dateYmd: string,
  startTime?: string,
  endTime?: string,
): string {
  const d = (dateYmd || "").trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return "";
  }
  const date = new Date(`${d}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const dateStr = date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const s = formatTimeForDisplay(startTime);
  const e = formatTimeForDisplay(endTime);
  if (s && e) {
    return `${dateStr} · ${s} – ${e}`;
  }
  if (s) {
    return `${dateStr} · ${s}`;
  }
  return dateStr;
}

function getCounterForProduct(
  list: CustomerProduct[] | undefined,
  productId: number,
): CustomerProduct | undefined {
  return (list ?? []).find((c) => c.ProductID === productId);
}

function cellDisplay(
  c: CustomerProduct,
  isChecked: boolean,
): { text: string; negative: boolean; disabled: boolean } {
  if (c.IsUnlimited) {
    return { text: "∞", negative: false, disabled: false };
  }
  const rem = c.RemainingCount;
  if (isChecked) {
    const after = rem - 1;
    return { text: String(after), negative: after < 0, disabled: rem <= 0 };
  }
  return { text: String(rem), negative: false, disabled: rem <= 0 };
}

// Match customer trainings from templates carried by product lines.
function getTrainingsForProductAndCustomer(
  product: CustomerProduct["Product"] | undefined,
  trainings: Training[] | undefined,
): Training[] {
  if (!product || !trainings || trainings.length === 0) return [];
  const seen = new Set<number>();
  const tmplIds: number[] = [];
  for (const line of product.Lines ?? []) {
    const ids = line.prestation?.TrainingTemplateIDs ?? [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      tmplIds.push(id);
    }
  }
  if (tmplIds.length === 0) return [];
  const out: Training[] = [];
  for (const tmplId of tmplIds) {
    const t = trainings.find((tr) => tr.TrainingTemplateID === tmplId && tr.ID != null);
    if (t) out.push(t);
  }
  return out;
}

function trainingShortLabel(t: Training): string {
  const q = t.Qualification?.trim();
  if (q) return q;
  if (t.Agency?.trim()) return t.Agency.trim();
  return `Formation #${t.TrainingTemplateID ?? "?"}`;
}

function trainingAllowsSessionIncrement(tr: Training): boolean {
  return (tr.Status ?? "") === "En cours";
}

function trainingAllowsSessionWithPending(
  tr: Training,
  pending: Readonly<Record<number, PendingStatusAction>>,
): boolean {
  const id = tr.ID;
  if (id == null) return false;
  if (pending[id]) return false;
  return trainingAllowsSessionIncrement(tr);
}

function findTrainingById(
  map: Record<number, Training[]>,
  trainingId: number,
): Training | undefined {
  for (const list of Object.values(map)) {
    const t = (list ?? []).find((x) => x.ID === trainingId);
    if (t) return t;
  }
  return undefined;
}

export default function ExcursionValidateCountersModal({
  open,
  onClose,
  excursionId,
  excursionName,
  excursionDate,
  startTime,
  endTime,
  customerGroups,
  onSuccess,
  onOpenCustomerOverview,
}: Props) {
  const contextSubtitle = React.useMemo(
    () => buildExcursionContextSubtitle(excursionDate, startTime, endTime),
    [excursionDate, startTime, endTime],
  );
  const mainTitle = `${excursionName.trim()} - Validation` || "Sortie";
  const subtitle = contextSubtitle || null;
  const { listCustomerCounters, batchConsume } = useBilling();
  const { getCustomersBatch } = useCustomer();
  const { batchIncrementTrainingSessions, cancelTraining, updateTraining } = useTraining();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditTrainings = hasPermission(Permission.TRAININGS_EDIT);
  const { config } = useAppSettings();
  const trainingStatusMenuActionList = React.useMemo(
    () => trainingStatusMenuActions(config?.CustomersSync === true, "inProgress"),
    [config?.CustomersSync],
  );
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [countersByCustomer, setCountersByCustomer] = React.useState<
    Record<number, CustomerProduct[]>
  >({});
  const [trainingsByCustomer, setTrainingsByCustomer] = React.useState<Record<number, Training[]>>(
    {},
  );
  const [checkedCpIds, setCheckedCpIds] = React.useState<Set<number>>(() => new Set());
  const [checkedTrainingIds, setCheckedTrainingIds] = React.useState<Set<number>>(() => new Set());
  const [pendingTrainingStatusById, setPendingTrainingStatusById] = React.useState<
    Record<number, PendingStatusAction>
  >({});
  const [purchaseForCustomerId, setPurchaseForCustomerId] = React.useState<number | null>(null);

  const uniqueCounterCustomerIds = React.useMemo(() => {
    const seen = new Set<number>();
    const out: number[] = [];

    for (const slice of customerGroups) {
      for (const row of slice.customers) {
        if (seen.has(row.customerId)) continue;

        seen.add(row.customerId);
        out.push(row.customerId);
      }
    }

    return out;
  }, [customerGroups]);

  const countersByCustomerRef = React.useRef(countersByCustomer);
  React.useEffect(() => {
    countersByCustomerRef.current = countersByCustomer;
  });

  const productColumns = React.useMemo((): ProductColumn[] => {
    const map = new Map<number, string>();

    for (const customerId of uniqueCounterCustomerIds) {
      for (const c of countersByCustomer[customerId] ?? []) {
        if (!map.has(c.ProductID)) {
          const name = c.Product?.Name?.trim() || `Produit #${c.ProductID}`;
          map.set(c.ProductID, name);
        }
      }
    }

    return Array.from(map.entries())
      .map(([productId, name]) => ({ productId, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));
  }, [uniqueCounterCustomerIds, countersByCustomer]);

  const loadAll = React.useCallback(async () => {
    if (uniqueCounterCustomerIds.length === 0) {
      setCountersByCustomer({});
      setTrainingsByCustomer({});
      setCheckedCpIds(new Set());
      setCheckedTrainingIds(new Set());
      setPendingTrainingStatusById({});
      setLoading(false);

      return;
    }

    setLoading(true);
    setCheckedCpIds(new Set());
    setCheckedTrainingIds(new Set());
    setPendingTrainingStatusById({});
    const nextCounters: Record<number, CustomerProduct[]> = {};
    const customerIds = uniqueCounterCustomerIds;
    const [, batch] = await Promise.all([
      Promise.all(
        uniqueCounterCustomerIds.map(async (customerId) => {
          const list = (await listCustomerCounters(customerId, "active")) ?? [];
          nextCounters[customerId] = list;
        }),
      ),
      getCustomersBatch(customerIds),
    ]);
    const nextTrainings: Record<number, Training[]> = {};
    if (batch) {
      for (const cust of batch) {
        if (cust.Id != null) nextTrainings[cust.Id] = cust.Trainings ?? [];
      }
    }
    setCountersByCustomer(nextCounters);
    setTrainingsByCustomer(nextTrainings);
    setLoading(false);
  }, [uniqueCounterCustomerIds, listCustomerCounters, getCustomersBatch]);

  React.useEffect(() => {
    if (!open) return;
    void loadAll();
  }, [open, loadAll]);

  React.useEffect(() => {
    if (!open) setPurchaseForCustomerId(null);
  }, [open]);

  React.useEffect(() => {
    setCheckedTrainingIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const tid of prev) {
        let tr: Training | undefined;
        for (const list of Object.values(trainingsByCustomer)) {
          tr = (list ?? []).find((t) => t.ID === tid);
          if (tr) break;
        }
        if (!tr || !trainingAllowsSessionWithPending(tr, pendingTrainingStatusById)) {
          next.delete(tid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [trainingsByCustomer, pendingTrainingStatusById]);

  const refreshOneCustomer = React.useCallback(
    async (customerId: number) => {
      const oldList = countersByCustomerRef.current[customerId] ?? [];
      const [list, batch] = await Promise.all([
        listCustomerCounters(customerId, "active"),
        getCustomersBatch([customerId]),
      ]);
      const safeList = list ?? [];
      setCheckedCpIds((prev) => {
        const n = new Set(prev);
        oldList.forEach((x) => n.delete(x.ID));
        return n;
      });
      setCountersByCustomer((p) => ({ ...p, [customerId]: safeList }));
      if (batch && batch.length > 0) {
        const fresh = batch[0];
        setTrainingsByCustomer((p) => ({ ...p, [customerId]: fresh.Trainings ?? [] }));
      }
    },
    [listCustomerCounters, getCustomersBatch],
  );

  const handleTrainingStatusAction = React.useCallback(
    (action: TrainingStatusAction, training: Training, _customerId: number) => {
      if (action === "valider" || action === "en_cours") {
        return;
      }
      const trId = training.ID;
      if (trId == null) {
        return;
      }
      if (action === "annuler") {
        setPendingTrainingStatusById((p) => ({ ...p, [trId]: "annuler" }));
        setCheckedTrainingIds((prev) => {
          const n = new Set(prev);
          n.delete(trId);
          return n;
        });
        return;
      }
      if (action === "terminer") {
        setPendingTrainingStatusById((p) => ({ ...p, [trId]: "terminer" }));
        setCheckedTrainingIds((prev) => {
          const n = new Set(prev);
          n.delete(trId);
          return n;
        });
      }
    },
    [],
  );

  const clearPendingTrainingStatus = React.useCallback((trainingId: number) => {
    setPendingTrainingStatusById((p) => {
      if (!(trainingId in p)) return p;
      const next = { ...p };
      delete next[trainingId];
      return next;
    });
  }, []);

  const toggleCounter = (cp: CustomerProduct) => {
    if (!cp.IsUnlimited && cp.RemainingCount <= 0) return;
    setCheckedCpIds((prev) => {
      const n = new Set(prev);
      if (n.has(cp.ID)) n.delete(cp.ID);
      else n.add(cp.ID);
      return n;
    });
  };

  const toggleTraining = (trainingId: number) => {
    let tr: Training | undefined;
    for (const list of Object.values(trainingsByCustomer)) {
      tr = (list ?? []).find((t) => t.ID === trainingId);
      if (tr) break;
    }
    if (!tr || !trainingAllowsSessionWithPending(tr, pendingTrainingStatusById)) return;
    setCheckedTrainingIds((prev) => {
      const n = new Set(prev);
      if (n.has(trainingId)) n.delete(trainingId);
      else n.add(trainingId);
      return n;
    });
  };

  const hasPendingStatusChanges = Object.keys(pendingTrainingStatusById).length > 0;
  const hasAtLeastOneAction =
    checkedCpIds.size > 0 || checkedTrainingIds.size > 0 || hasPendingStatusChanges;

  const applyCounterValidation = async () => {
    const pendingSnapshot: Record<number, PendingStatusAction> = { ...pendingTrainingStatusById };
    const hasPending = Object.keys(pendingSnapshot).length > 0;
    if (checkedCpIds.size === 0 && checkedTrainingIds.size === 0 && !hasPending) {
      showAlert(
        "Cochez au moins un compteur ou enregistrez un changement de statut. Rien n'a été appliqué.",
        "warning",
      );
      return;
    }
    setSubmitting(true);
    if (checkedCpIds.size > 0) {
      const items = [...checkedCpIds].map((customer_product_id) => ({ customer_product_id }));
      const results = await batchConsume({ excursion_id: excursionId, items });
      if (results) {
        const failed = results.filter((r) => !r.success);
        if (failed.length > 0) {
          showAlert(
            failed.map((f) => f.error || `Compteur #${f.customer_product_id}`).join(" · "),
            "danger",
          );
          setSubmitting(false);
          return;
        }
      } else {
        setSubmitting(false);
        return;
      }
    }
    if (checkedTrainingIds.size > 0) {
      const sessionIds: number[] = [];
      for (const tid of checkedTrainingIds) {
        const t = findTrainingById(trainingsByCustomer, tid);
        if (t && trainingAllowsSessionWithPending(t, pendingTrainingStatusById)) {
          sessionIds.push(tid);
        }
      }
      if (sessionIds.length > 0) {
        const updated = await batchIncrementTrainingSessions(sessionIds);
        if (updated === undefined) {
          setSubmitting(false);
          return;
        }
      }
    }
    if (hasPending) {
      const entries = Object.entries(pendingSnapshot) as [string, PendingStatusAction][];
      for (const [sid, kind] of entries) {
        const id = Number(sid);
        const tr = findTrainingById(trainingsByCustomer, id);
        if (tr == null) continue;
        try {
          if (kind === "annuler") {
            await cancelTraining(tr, {
              skipConfirmation: true,
              rethrowOnError: true,
              suppressSuccessAlert: true,
            });
          } else {
            await updateTraining(
              {
                ...tr,
                Status: "Terminé",
                UpdatedBy: userDisplayName(user),
                UpdatedAt: new Date(),
              },
              { rethrowOnError: true, suppressSuccessAlert: true },
            );
          }
        } catch {
          setSubmitting(false);
          showAlert(
            "Erreur lors de l'enregistrement des changements de statut de formation.",
            "danger",
          );
          return;
        }
      }
      setPendingTrainingStatusById({});
      showAlert("Statuts de formation enregistrés.", "success");
    }
    setSubmitting(false);
    onSuccess();
  };

  const handleConfirm = async () => {
    if (checkedCpIds.size === 0 && checkedTrainingIds.size === 0 && !hasPendingStatusChanges) {
      showAlert(
        "Cochez au moins un compteur ou enregistrez un changement de statut. Rien n'a été appliqué.",
        "warning",
      );
      return;
    }
    const ok = await showConfirmation(
      "Confirmer le décompte",
      "Valider la sortie et les compteurs ?",
    );
    if (!ok) return;
    await applyCounterValidation();
  };

  const stickyFirstCellSx = {
    position: "sticky" as const,
    left: 0,
    zIndex: 2,
    background: colorTokens.bg.surfaceMuted,
    boxShadow: "4px 0 8px -4px rgba(0,0,0,0.2)",
  };
  const stickyHeaderFirstSx = { ...stickyFirstCellSx, zIndex: 3 };

  return (
    <>
      <CommonModal
        open={open}
        onClose={onClose}
        title={mainTitle}
        subtitle={subtitle}
        size="xl"
        onConfirm={handleConfirm}
        confirmLabel="Valider le décompte"
        submitDisabled={loading || !hasAtLeastOneAction}
        loading={submitting}
        disableEnforceFocus
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size="md" />
          </Box>
        )}
        {!loading && customerGroups.length > 0 && productColumns.length === 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Sheet
              variant="outlined"
              sx={{
                overflow: "auto",
                borderColor: colorTokens.border.default,
                backgroundColor: colorTokens.bg.surfaceMuted,
                p: 1,
              }}
            >
              <Table size="sm" sx={{ ...hrTableSx }}>
                <tbody>
                  {customerGroups.map((slice, sliceIndex) => (
                    <React.Fragment key={`cg-plain-${sliceIndex}`}>
                      <tr>
                        <Box
                          component="td"
                          colSpan={2}
                          sx={{
                            py: 1,
                            borderBottom: `1px solid ${colorTokens.border.default}`,
                            backgroundColor: colorTokens.bg.surface,
                          }}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <DarkTypography level="title-sm" colorVariant="title">
                              {slice.groupTitle}
                            </DarkTypography>
                            <DarkTypography level="body-xs" colorVariant="muted">
                              Encadrants : {slice.guideLine}
                            </DarkTypography>
                          </Box>
                        </Box>
                      </tr>
                      {slice.customers.map(({ customerId, label, qualification }, rowIndex) => (
                        <tr key={`cg-plain-${sliceIndex}-${customerId}-${rowIndex}`}>
                          <td>
                            <ExcursionCounterClientName
                              label={label}
                              qualification={qualification}
                              onOpenCustomerOverview={
                                onOpenCustomerOverview != null
                                  ? () => onOpenCustomerOverview(customerId)
                                  : undefined
                              }
                            />
                          </td>
                          <td>
                            <IconButtonCTA
                              size="sm"
                              ctaVariant="accent"
                              aria-label={purchaseProductAriaLabel(label, qualification)}
                              onClick={() => setPurchaseForCustomerId(customerId)}
                            >
                              <SCreate sx={{ fontSize: "1.15rem" }} />
                            </IconButtonCTA>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          </Box>
        )}
        {!loading && customerGroups.length > 0 && productColumns.length > 0 && (
          <Sheet
            variant="outlined"
            sx={{
              overflow: "auto",
              maxWidth: "100%",
              borderColor: colorTokens.border.default,
              backgroundColor: colorTokens.bg.surfaceMuted,
            }}
          >
            <Table
              size="sm"
              stickyHeader
              sx={{
                ...hrTableSx,
                minWidth: Math.max(440, 160 + productColumns.length * 168 + 120),
                "& th": {
                  color: textTokens.color.dimmed,
                  fontSize: "0.7rem",
                  whiteSpace: "normal",
                },
                "& thead th:first-of-type": stickyHeaderFirstSx,
                "& td": { verticalAlign: "middle" },
                "& td:first-of-type": stickyFirstCellSx,
              }}
            >
              <thead>
                <tr>
                  <th>Client</th>
                  {productColumns.map((col) => (
                    <th key={col.productId} style={{ minWidth: 148, maxWidth: 220 }}>
                      {col.name}
                    </th>
                  ))}
                  <th style={{ minWidth: 88 }}> </th>
                </tr>
              </thead>
              <tbody>
                {customerGroups.map((slice, sliceIndex) => (
                  <React.Fragment key={`cg-rows-${sliceIndex}`}>
                    <tr>
                      <Box
                        component="td"
                        colSpan={2 + productColumns.length}
                        sx={{
                          py: 1,
                          borderBottom: `1px solid ${colorTokens.border.default}`,
                          backgroundColor: colorTokens.bg.surface,
                          position: "static",
                          left: "auto",
                          zIndex: 1,
                          boxShadow: "none",
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <DarkTypography level="title-sm" colorVariant="title">
                            {slice.groupTitle}
                          </DarkTypography>
                          <DarkTypography level="body-xs" colorVariant="muted">
                            Encadrants : {slice.guideLine}
                          </DarkTypography>
                        </Box>
                      </Box>
                    </tr>
                    {slice.customers.map(({ customerId, label, qualification }, rowIndex) => {
                      const list = countersByCustomer[customerId] ?? [];

                      return (
                        <tr key={`cg-rows-${sliceIndex}-${customerId}-${rowIndex}`}>
                          <td>
                            <ExcursionCounterClientName
                              label={label}
                              qualification={qualification}
                              onOpenCustomerOverview={
                                onOpenCustomerOverview != null
                                  ? () => onOpenCustomerOverview(customerId)
                                  : undefined
                              }
                            />
                          </td>
                          {productColumns.map((col) => {
                            const c = getCounterForProduct(list, col.productId);
                            if (c == null) {
                              return (
                                <td key={col.productId}>
                                  <DarkTypography level="body-xs" colorVariant="muted">
                                    —
                                  </DarkTypography>
                                </td>
                              );
                            }
                            const checked = checkedCpIds.has(c.ID);
                            const { text, negative, disabled } = cellDisplay(c, checked);
                            const creditText =
                              c.IsUnlimited || !checked
                                ? text
                                : `${c.RemainingCount} → ${c.RemainingCount - 1}`;
                            const linkedTrainings = getTrainingsForProductAndCustomer(
                              c.Product,
                              trainingsByCustomer[customerId],
                            );

                            return (
                              <td key={`${customerId}-${col.productId}`}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.25,
                                    }}
                                  >
                                    <DarkTypography
                                      level="body-xs"
                                      colorVariant="muted"
                                      sx={{
                                        fontSize: "0.65rem",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                      }}
                                    >
                                      Crédits
                                    </DarkTypography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Checkbox
                                        size="sm"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => toggleCounter(c)}
                                        sx={{ p: 0, minWidth: 28 }}
                                        aria-label={`Décompter ${col.name} pour ${label}${
                                          qualification?.trim() ? ` — ${qualification.trim()}` : ""
                                        }`}
                                      />
                                      <DarkTypography
                                        level="body-sm"
                                        component="span"
                                        colorVariant="secondary"
                                        sx={{
                                          fontVariantNumeric: "tabular-nums",
                                          color: negative ? colorTokens.status.danger : undefined,
                                          fontWeight: checked ? 700 : 500,
                                        }}
                                      >
                                        {creditText}
                                      </DarkTypography>
                                    </Box>
                                  </Box>
                                  {linkedTrainings.length > 0 && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.25,
                                        borderLeft: `2px solid ${colorTokens.border.default}`,
                                        ml: 0.25,
                                        pl: 0.75,
                                      }}
                                    >
                                      <DarkTypography
                                        level="body-xs"
                                        colorVariant="muted"
                                        sx={{
                                          fontSize: "0.65rem",
                                          textTransform: "uppercase",
                                          letterSpacing: 0.4,
                                        }}
                                      >
                                        Séances
                                      </DarkTypography>
                                      {linkedTrainings.map((tr) => {
                                        const trId = tr.ID as number;
                                        const trChecked = checkedTrainingIds.has(trId);
                                        const allowSessions = trainingAllowsSessionWithPending(
                                          tr,
                                          pendingTrainingStatusById,
                                        );
                                        const pendingSt = pendingTrainingStatusById[trId];
                                        const showStatusSelect =
                                          canEditTrainings && (allowSessions || Boolean(pendingSt));
                                        const sessions = tr.Sessions ?? 0;
                                        const afterSessions = sessions + 1;
                                        const shortLabel = trainingShortLabel(tr);

                                        return (
                                          <Box
                                            key={`tr-${trId}`}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                              flexWrap: "nowrap",
                                              width: "100%",
                                              minWidth: 0,
                                            }}
                                          >
                                            {allowSessions ? (
                                              <Checkbox
                                                size="sm"
                                                checked={trChecked}
                                                onChange={() => toggleTraining(trId)}
                                                sx={{ p: 0, minWidth: 24, flexShrink: 0 }}
                                                aria-label={`Incrémenter le compteur de séances pour ${shortLabel}, client ${label} (${sessions} → ${afterSessions} si coché)`}
                                              />
                                            ) : null}
                                            <DarkTypography
                                              level="body-xs"
                                              component="span"
                                              colorVariant="secondary"
                                              sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontVariantNumeric: "tabular-nums",
                                                fontWeight: trChecked ? 700 : 500,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                opacity: allowSessions ? 1 : 0.75,
                                              }}
                                            >
                                              {pendingSt ? (
                                                <Box
                                                  component="span"
                                                  sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    minWidth: 0,
                                                    maxWidth: "100%",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <Box
                                                    component="span"
                                                    sx={{
                                                      minWidth: 0,
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                  >
                                                    {shortLabel}
                                                  </Box>
                                                  <Box
                                                    component="span"
                                                    aria-label={
                                                      pendingSt === "terminer"
                                                        ? "Clôture à la validation du décompte"
                                                        : "Annulation à la validation du décompte"
                                                    }
                                                    sx={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      flexShrink: 0,
                                                      fontSize: "1.1rem",
                                                      lineHeight: 1,
                                                    }}
                                                  >
                                                    {pendingStatusRowEmoji(pendingSt)}
                                                  </Box>
                                                </Box>
                                              ) : (
                                                <>
                                                  {shortLabel}
                                                  {allowSessions
                                                    ? `: ${
                                                        trChecked
                                                          ? `${sessions} → ${afterSessions}`
                                                          : sessions
                                                      }`
                                                    : tr.Status
                                                      ? ` (${tr.Status})`
                                                      : ""}
                                                </>
                                              )}
                                            </DarkTypography>
                                            {showStatusSelect ? (
                                              <Box
                                                sx={{
                                                  flexShrink: 0,
                                                  ml: "auto",
                                                  display: "inline-flex",
                                                }}
                                              >
                                                <TrainingStatusActionSelect
                                                  listKey={`${customerId}-${col.productId}-tr-${trId}`}
                                                  allowedActions={trainingStatusMenuActionList}
                                                  inlineCompact
                                                  pendingAction={pendingSt ?? null}
                                                  onClearPending={
                                                    pendingSt
                                                      ? () => clearPendingTrainingStatus(trId)
                                                      : undefined
                                                  }
                                                  onAction={(a) =>
                                                    void handleTrainingStatusAction(
                                                      a,
                                                      tr,
                                                      customerId,
                                                    )
                                                  }
                                                />
                                              </Box>
                                            ) : null}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>
                              </td>
                            );
                          })}
                          <td>
                            <IconButtonCTA
                              size="sm"
                              ctaVariant="accent"
                              aria-label={purchaseProductAriaLabel(label, qualification)}
                              onClick={() => setPurchaseForCustomerId(customerId)}
                            >
                              <SCreate sx={{ fontSize: "1.15rem" }} />
                            </IconButtonCTA>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Sheet>
        )}
      </CommonModal>
      {purchaseForCustomerId != null && (
        <PurchaseProductModal
          open
          onClose={() => setPurchaseForCustomerId(null)}
          customerId={purchaseForCustomerId}
          onPurchased={async () => {
            await refreshOneCustomer(purchaseForCustomerId);
            setPurchaseForCustomerId(null);
          }}
        />
      )}
    </>
  );
}
